/**
 * Mileage Simulator Calculation Engine
 * Implements all business logic for cost and profit calculations
 */

import type {
  FlightLeg,
  MileageQuotation,
  CostBreakdown,
  ProfitCalculation,
  ProfitAnalysis,
} from '../types';
import { PaymentMethod, PAYMENT_FEES } from '../types';

/**
 * Calculate the cost for a single flight leg
 * Formula: (Miles / 1000) * CostPerThousand + BoardingTax
 */
export function calculateLegCost(leg: FlightLeg): number {
  const mileCost = (leg.miles / 1000) * leg.costPerThousand;
  return mileCost + leg.boardingTax;
}

/**
 * Calculate complete cost breakdown for a quotation
 */
export function calculateCostBreakdown(quotation: MileageQuotation): CostBreakdown {
  const outboundCost = calculateLegCost(quotation.outbound);
  const returnCost = quotation.return ? calculateLegCost(quotation.return) : 0;
  const totalCost = outboundCost + returnCost;
  const savings = Math.max(0, quotation.airlinePrice - totalCost);
  const savingsPercentage = quotation.airlinePrice > 0
    ? (savings / quotation.airlinePrice) * 100
    : 0;

  return {
    outboundCost,
    returnCost,
    totalCost,
    savings,
    savingsPercentage,
  };
}

/**
 * Calculate profit for PIX payment (0% fee)
 */
function calculatePixProfit(
  chargedAmount: number,
  totalCost: number,
  airlinePrice: number
): ProfitCalculation {
  const feePercentage = PAYMENT_FEES.PIX;
  const feeAmount = 0;
  const receivedAmount = chargedAmount;
  const grossProfit = receivedAmount - totalCost;
  const grossProfitPercentage = chargedAmount > 0
    ? (grossProfit / chargedAmount) * 100
    : 0;

  return {
    paymentMethod: PaymentMethod.PIX,
    chargedAmount,
    receivedAmount,
    feePercentage,
    feeAmount,
    grossProfit,
    grossProfitPercentage,
    isProfitable: grossProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit for Card 1x payment (2.99% fee)
 */
function calculateCard1xProfit(
  chargedAmount: number,
  totalCost: number,
  airlinePrice: number
): ProfitCalculation {
  const feePercentage = PAYMENT_FEES.CARD_1X;
  const feeAmount = chargedAmount * feePercentage;
  const receivedAmount = chargedAmount - feeAmount;
  const grossProfit = receivedAmount - totalCost;
  const grossProfitPercentage = chargedAmount > 0
    ? (grossProfit / chargedAmount) * 100
    : 0;

  return {
    paymentMethod: PaymentMethod.CARD_1X,
    installments: 1,
    chargedAmount,
    receivedAmount,
    feePercentage,
    feeAmount,
    grossProfit,
    grossProfitPercentage,
    isProfitable: grossProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit for Card installment payment
 * Fee formula: chargedAmount * (1 - (installments * CARD_INSTALLMENT_RATE))
 */
function calculateCardInstallmentProfit(
  chargedAmount: number,
  totalCost: number,
  airlinePrice: number,
  installments: number
): ProfitCalculation {
  const totalFeeRate = installments * PAYMENT_FEES.CARD_INSTALLMENT_RATE;
  const feeAmount = chargedAmount * totalFeeRate;
  const receivedAmount = chargedAmount * (1 - totalFeeRate);
  const grossProfit = receivedAmount - totalCost;
  const grossProfitPercentage = chargedAmount > 0
    ? (grossProfit / chargedAmount) * 100
    : 0;

  return {
    paymentMethod: PaymentMethod.CARD_INSTALLMENTS,
    installments,
    chargedAmount,
    receivedAmount,
    feePercentage: totalFeeRate,
    feeAmount,
    grossProfit,
    grossProfitPercentage,
    isProfitable: grossProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit for PIX based on desired profit
 * The amount received is always Cost + Desired Profit (no fees)
 */
function calculatePixProfitFromDesiredProfit(
  totalCost: number,
  desiredProfit: number,
  airlinePrice: number
): ProfitCalculation {
  const receivedAmount = totalCost + desiredProfit;
  const chargedAmount = receivedAmount; // No fees for PIX
  const feeAmount = 0;
  const feePercentage = PAYMENT_FEES.PIX;

  return {
    paymentMethod: PaymentMethod.PIX,
    chargedAmount,
    receivedAmount,
    feePercentage,
    feeAmount,
    grossProfit: desiredProfit,
    grossProfitPercentage: chargedAmount > 0 ? (desiredProfit / chargedAmount) * 100 : 0,
    isProfitable: desiredProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit for Card 1x based on desired profit
 * The amount received is always Cost + Desired Profit
 * Fees are added on top and charged to the customer
 */
function calculateCard1xProfitFromDesiredProfit(
  totalCost: number,
  desiredProfit: number,
  airlinePrice: number
): ProfitCalculation {
  const receivedAmount = totalCost + desiredProfit;
  const chargedAmount = receivedAmount / (1 - PAYMENT_FEES.CARD_1X);
  const feeAmount = chargedAmount - receivedAmount;
  const feePercentage = PAYMENT_FEES.CARD_1X;

  return {
    paymentMethod: PaymentMethod.CARD_1X,
    installments: 1,
    chargedAmount,
    receivedAmount,
    feePercentage,
    feeAmount,
    grossProfit: desiredProfit,
    grossProfitPercentage: chargedAmount > 0 ? (desiredProfit / chargedAmount) * 100 : 0,
    isProfitable: desiredProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit for Card Installments based on desired profit
 * The amount received is always Cost + Desired Profit
 * Fees are added on top and charged to the customer
 */
function calculateCardInstallmentProfitFromDesiredProfit(
  totalCost: number,
  desiredProfit: number,
  airlinePrice: number,
  installments: number
): ProfitCalculation {
  const receivedAmount = totalCost + desiredProfit;
  const totalFeeRate = installments * PAYMENT_FEES.CARD_INSTALLMENT_RATE;
  const chargedAmount = receivedAmount / (1 - totalFeeRate);
  const feeAmount = chargedAmount - receivedAmount;

  return {
    paymentMethod: PaymentMethod.CARD_INSTALLMENTS,
    installments,
    chargedAmount,
    receivedAmount,
    feePercentage: totalFeeRate,
    feeAmount,
    grossProfit: desiredProfit,
    grossProfitPercentage: chargedAmount > 0 ? (desiredProfit / chargedAmount) * 100 : 0,
    isProfitable: desiredProfit > 0,
    exceedsAirlinePrice: chargedAmount > airlinePrice,
  };
}

/**
 * Calculate profit analysis based on desired profit
 * This calculates the prices to charge for each payment method
 */
export function calculateProfitAnalysisFromDesiredProfit(
  quotation: MileageQuotation,
  desiredProfit: number
): ProfitAnalysis {
  const costs = calculateCostBreakdown(quotation);

  // PIX calculation - receivedAmount is always cost + desiredProfit
  const pix = calculatePixProfitFromDesiredProfit(
    costs.totalCost,
    desiredProfit,
    quotation.airlinePrice
  );

  // Card 1x calculation - receivedAmount is always cost + desiredProfit
  const card1x = calculateCard1xProfitFromDesiredProfit(
    costs.totalCost,
    desiredProfit,
    quotation.airlinePrice
  );

  // Card installments (2x to 12x) - 1x is already covered by card1x
  // Only show installments where price doesn't exceed airline price
  const cardInstallments: ProfitCalculation[] = [];
  for (let i = 2; i <= 12; i++) {
    const installmentOption = calculateCardInstallmentProfitFromDesiredProfit(
      costs.totalCost,
      desiredProfit,
      quotation.airlinePrice,
      i
    );

    // Only add installment if the calculated price doesn't exceed airline price
    if (installmentOption.chargedAmount <= quotation.airlinePrice) {
      cardInstallments.push(installmentOption);
    }
  }

  // Find best option (one that doesn't exceed airline price, or lowest price if all exceed)
  const allOptions = [pix, card1x, ...cardInstallments];
  const viableOptions = allOptions.filter(opt => !opt.exceedsAirlinePrice);
  const bestOption = viableOptions.length > 0
    ? viableOptions[0] // PIX is always first and best if viable
    : allOptions.reduce((best, current) =>
        current.chargedAmount < best.chargedAmount ? current : best
      );

  return {
    costs,
    desiredChargeAmount: desiredProfit,
    pix,
    card1x,
    cardInstallments,
    bestOption,
  };
}

/**
 * Calculate complete profit analysis for all payment methods
 */
export function calculateProfitAnalysis(
  quotation: MileageQuotation,
  desiredChargeAmount: number
): ProfitAnalysis {
  const costs = calculateCostBreakdown(quotation);

  // PIX calculation
  const pix = calculatePixProfit(
    desiredChargeAmount,
    costs.totalCost,
    quotation.airlinePrice
  );

  // Card 1x calculation
  const card1x = calculateCard1xProfit(
    desiredChargeAmount,
    costs.totalCost,
    quotation.airlinePrice
  );

  // Card installments (2x to 12x) - 1x is already covered by card1x
  const cardInstallments: ProfitCalculation[] = [];
  for (let i = 2; i <= 12; i++) {
    cardInstallments.push(
      calculateCardInstallmentProfit(
        desiredChargeAmount,
        costs.totalCost,
        quotation.airlinePrice,
        i
      )
    );
  }

  // Find best option (highest gross profit)
  const allOptions = [pix, card1x, ...cardInstallments];
  const bestOption = allOptions.reduce((best, current) =>
    current.grossProfit > best.grossProfit ? current : best
  );

  return {
    costs,
    desiredChargeAmount,
    pix,
    card1x,
    cardInstallments,
    bestOption,
  };
}

/**
 * Validate if quotation is viable
 */
export function validateQuotation(quotation: MileageQuotation): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (quotation.airlinePrice <= 0) {
    errors.push('O preço da companhia aérea deve ser maior que zero');
  }

  if (quotation.outbound.miles <= 0) {
    errors.push('A quantidade de milhas da ida deve ser maior que zero');
  }

  if (quotation.outbound.costPerThousand <= 0) {
    errors.push('O custo do milheiro da ida deve ser maior que zero');
  }

  if (quotation.outbound.boardingTax < 0) {
    errors.push('A taxa de embarque da ida não pode ser negativa');
  }

  if (quotation.isRoundTrip && quotation.return) {
    if (quotation.return.miles <= 0) {
      errors.push('A quantidade de milhas da volta deve ser maior que zero');
    }

    if (quotation.return.costPerThousand <= 0) {
      errors.push('O custo do milheiro da volta deve ser maior que zero');
    }

    if (quotation.return.boardingTax < 0) {
      errors.push('A taxa de embarque da volta não pode ser negativa');
    }
  }

  const costs = calculateCostBreakdown(quotation);
  if (costs.totalCost >= quotation.airlinePrice) {
    errors.push(
      'O custo total das milhas é maior ou igual ao preço da companhia aérea. Esta operação não é vantajosa.'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Format currency value to BRL
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Format miles with thousand separators
 */
export function formatMiles(miles: number): string {
  return new Intl.NumberFormat('pt-BR').format(miles);
}

/**
 * Format percentage with 2 decimal places
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Get profit color class based on value
 */
export function getProfitColorClass(profit: number, isProfit: boolean = true): string {
  if (!isProfit || profit < 0) {
    return 'text-red-500';
  }
  return 'text-emerald-500';
}

/**
 * Get background profit color class based on value
 */
export function getProfitBgColorClass(profit: number, isProfit: boolean = true): string {
  if (!isProfit || profit < 0) {
    return 'bg-red-500/10 border-red-500/20';
  }
  return 'bg-emerald-500/10 border-emerald-500/20';
}
